import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { DayOfWeek } from '@blaze-shared/api-contracts-domain';
import { ButtonIconComponent, ClickableDirective } from '@blaze-shared/shared-ui';

import {
  AvailabilityScheduleConfig,
  AvailabilityScheduleRule,
  AvailabilityScheduleRuleTimeSlot,
} from '../../availability.model';
import { TimesRangePickerComponent } from '../../components/times-range-picker/times-range-picker.component';

@Component({
  selector: 'blaze-availability-schedule-rule',
  templateUrl: './availability-schedule-rule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonIconComponent,
    ClickableDirective,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    TimesRangePickerComponent,
  ],
  host: { class: 'flex items-start gap-24' },
})
export class AvailabilityScheduleRuleComponent {
  config = input.required<AvailabilityScheduleConfig>();
  rule = input.required<AvailabilityScheduleRule>();
  ruleIndex = input.required<number>();
  errors = input.required<string[] | undefined>();
  visibleDays = input.required<DayOfWeek[]>();
  defaultTimeSlot = input.required<AvailabilityScheduleRuleTimeSlot>();

  updateConfig = output<AvailabilityScheduleConfig>();
  copySlots = output<DayOfWeek>();

  protected onRuleEnabledChange(day: DayOfWeek): void {
    this.emitUpdateConfig(day, (rule) => {
      const enabled = !rule.enabled;
      const timeSlots = enabled ? [{ ...this.defaultTimeSlot() }] : [];
      return { ...rule, enabled, timeSlots };
    });
  }

  protected onAddSlot(day: DayOfWeek): void {
    this.emitUpdateConfig(day, (rule) => {
      const timeSlots = [...rule.timeSlots, this.getNewTimeSlot(rule.timeSlots)];
      return { ...rule, timeSlots };
    });
  }

  protected onDeleteSlot(day: DayOfWeek, slotIndex: number): void {
    this.emitUpdateConfig(day, (rule) => {
      const timeSlots = rule.timeSlots.filter((_, sIdx) => sIdx !== slotIndex);
      return { ...rule, timeSlots };
    });
  }

  protected onSlotTimeChange(
    event: { from: string; to: string },
    day: DayOfWeek,
    slotIndex: number,
  ): void {
    this.emitUpdateConfig(day, (rule) => {
      const timeSlots = rule.timeSlots.map((ts, sIdx) =>
        sIdx !== slotIndex ? ts : { ...ts, ...event },
      );
      return { ...rule, timeSlots };
    });
  }

  private emitUpdateConfig(
    day: DayOfWeek,
    updateRule: (rule: AvailabilityScheduleRule) => AvailabilityScheduleRule,
  ): void {
    const value = this.config();
    this.updateConfig.emit({
      ...value,
      rules: value.rules.map((rule) => {
        if (rule.day !== day) {
          return rule;
        }
        return updateRule(rule);
      }),
    });
  }

  private getNewTimeSlot(
    slots: AvailabilityScheduleRuleTimeSlot[],
  ): AvailabilityScheduleRuleTimeSlot {
    const newSlot = { ...this.defaultTimeSlot() };
    if (slots.length) {
      newSlot.from = slots[slots.length - 1].to;
      newSlot.to = incrementSlotHour(newSlot.from);
    }
    return newSlot;
  }
}

function incrementSlotHour(time: string): string {
  const [h, ...m] = time.split(':');
  let updated = parseInt(h, 10) + 1;
  if (updated === 24) {
    updated = 0;
  }
  m.unshift(`0${updated}`.substr(-2, 2));
  return m.join(':');
}
